import React from 'react';
import styled from 'styled-components';
import {SectionIntroWithCards, SectionIntroWithCardsProps} from '../SectionIntroWithCards';
import {AccentColorOptions} from 'epic-ue-theme';
import {GradientWrapper} from '../../gradient';
import {LocalVideoOptionsArray} from '../../../../types/background.types';
import {Video} from '../../video';
import {isValidMediaOptions} from '../../../../utils';

export const Styled = styled.div<{
    accentColor: AccentColorOptions;
    oneCard: boolean;
}>`
    position: relative;

    .heading.h4 {
        color: ${(p) => p.theme.palette.common.black};
    }

    .item-number {
        color: ${(p) => p.theme.palette.common.black};
    }

    .description .paragraph {
        color: ${(p) => p.theme.palette.common.black};
        a {
            color: ${(p) => p.theme.palette.common.white};
            &:hover {
                color: unset;
                &:after {
                    background: ${(p) => p.theme.palette.neutral.aluminium};
                }
            }
        }
    }

    .ue-outlined-card.${(p) => p.accentColor} {
        color: ${(p) => p.theme.palette.common.black};
        border-color: ${(p) => p.theme.palette.common.white};
        border-image-source: unset;
        min-height: ${(p) => (p.oneCard ? '0' : '494px')};
        max-width: ${(p) => (p.oneCard ? '520px' : '416px')};
        padding: 48px;
        ${(p) => p.theme.breakpoints.down('sm')} {
            max-width: 100%;
        }
        ${(p) => p.theme.breakpoints.down('md')} {
            padding: 24px;
            min-height: initial;
        }
        .card-content {
            .h4,
            .h3,
            .h2,
            .paragraph {
                color: ${(p) => p.theme.palette.common.black};
            }
        }
        .ue-icon-arrow-right-alt {
            color: ${(p) => p.theme.palette.accent.dark[p.accentColor]};
        }
        &:before {
            background-color: ${(p) => p.theme.palette.common.white};
            background-image: unset;
        }
        &:hover {
            background-color: ${(p) => p.theme.palette.common.white};
        }
    }

    &.gradient {
        padding: ${(p) => `${p.theme.spacing(12)}px`} 0;

        ${(p) => p.theme.breakpoints.down('xs')} {
            padding: ${(p) => `${p.theme.spacing(7)}px`} 0;
        }
    }

    &&.video {
        background: black;
        color: ${(p) => p.theme.palette.common.white};
        padding: ${(p) => `${p.theme.spacing(12)}px`} 0 ${(p) => `${p.theme.spacing(16)}px`};

        ${(p) => p.theme.breakpoints.down('xs')} {
            padding: ${(p) => `${p.theme.spacing(7)}px`} 0;
        }

        .ue-outlined-card {
            color: ${(p) => p.theme.palette.common.black};
            background-color: ${(p) => p.theme.palette.neutral.aluminium};
            transition: color 0.25s ${(p) => p.theme.easing},
                background-color 0.25s ${(p) => p.theme.easing};
        }

        .ue-outlined-card.${(p) => p.accentColor}.clickable:hover {
            border-color: ${(p) => p.theme.palette.common.black};
            background-color: ${(p) => p.theme.palette.common.black};
            a,
            div,
            .h2,
            .h3,
            .h4,
            .paragraph {
                color: ${(p) => p.theme.palette.common.white};
            }
            .ue-icon-arrow-right-alt {
                color: ${(p) => p.theme.palette.accent.bright[p.accentColor]};
            }
            &:before {
                background-color: ${(p) => p.theme.palette.common.black};
            }
        }

        .section-intro-with-cards {
            position: relative;
            z-index: 2;
        }
        .video-wrapper {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
        }
    }
`;

const VideoGradient = styled.div<{opacity?: number}>`
    background: rgb(28, 26, 31);
    background: linear-gradient(315deg, rgba(28, 26, 31, 0) 0%, rgba(28, 26, 31, 1) 100%);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: ${(p) => p.opacity || 0.6};
`;
export interface DownloadSectionProps extends SectionIntroWithCardsProps {
    variant?: 'gradient' | 'standard' | 'video';
    mediaOptions?: LocalVideoOptionsArray;
    gradientOpacity?: number;
}

export const DownloadSection = ({
    accentColor = 'forest',
    variant = 'gradient',
    mediaOptions,
    gradientOpacity = 0.6,
    cards,
    ...rest
}: DownloadSectionProps): JSX.Element | null => {
    if (variant === 'video' && !isValidMediaOptions(mediaOptions)) return null;
    if (!cards || !cards?.length) return null;
    return (
        <GradientWrapper
            disable={variant === 'standard'}
            variant="radial"
            accentColor={accentColor}>
            <Styled
                className={`ue-download-section ${variant}`}
                accentColor={accentColor}
                oneCard={cards?.length === 1}>
                {variant === 'video' ? (
                    <>
                        <Video mediaOptions={mediaOptions} mute />
                        <VideoGradient opacity={gradientOpacity} />
                    </>
                ) : (
                    ''
                )}
                <SectionIntroWithCards
                    accentColor={accentColor}
                    largeCards
                    cards={cards}
                    centerCards={cards?.length === 1}
                    {...rest}
                />
            </Styled>
        </GradientWrapper>
    );
};
